<script setup>
import { ref, computed, onMounted, reactive } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useAlert } from 'dashboard/composables';
import { debounce } from '@chatwoot/utils';
import { useCompaniesStore } from 'dashboard/stores/companies';

import CompaniesListLayout from 'dashboard/components-next/Companies/CompaniesListLayout.vue';
import CompaniesTable from 'dashboard/components-next/Companies/CompaniesTable.vue';
import CompanyCreateDialog from 'dashboard/components-next/Companies/CompanyCreateDialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const DEFAULT_SORT_FIELD = 'name';
const DEBOUNCE_DELAY = 300;

const companiesStore = useCompaniesStore();

const route = useRoute();
const router = useRouter();
const { t } = useI18n();

const { updateUISettings, uiSettings } = useUISettings();

const companies = computed(() => companiesStore.getCompaniesList);
const meta = computed(() => companiesStore.getMeta);
const uiFlags = computed(() => companiesStore.getUIFlags);

const searchQuery = computed(() => route.query?.search || '');
const searchValue = ref(searchQuery.value);
const createCompanyDialogRef = ref(null);
const bulkDeleteDialogRef = ref(null);
const selectedCompanyIds = ref([]);
const isBulkActionLoading = ref(false);
const pageNumber = computed(() => Number(route.query?.page) || 1);
const selectedCount = computed(() => selectedCompanyIds.value.length);
const visibleCompanyIds = computed(() =>
  companies.value.map(company => company.id)
);

const parseSortSettings = (sortString = '') => {
  const hasDescending = sortString.startsWith('-');
  const sortField = hasDescending ? sortString.slice(1) : sortString;
  return {
    sort: sortField || DEFAULT_SORT_FIELD,
    order: hasDescending ? '-' : '',
  };
};

const { companies_sort_by: companySortBy = DEFAULT_SORT_FIELD } =
  uiSettings.value ?? {};
const { sort: initialSort, order: initialOrder } =
  parseSortSettings(companySortBy);

const sortState = reactive({
  activeSort: initialSort,
  activeOrdering: initialOrder,
});

const activeSort = computed(() => sortState.activeSort);
const activeOrdering = computed(() => sortState.activeOrdering);

const isFetchingList = computed(() => uiFlags.value.fetchingList);
const isCreatingCompany = computed(() => uiFlags.value.creatingItem);

const buildSortAttr = () =>
  `${sortState.activeOrdering}${sortState.activeSort}`;

const sortParam = computed(() => buildSortAttr());

const updateURLParams = (page, search = '', sort = '') => {
  const query = {
    ...route.query,
    page: page.toString(),
  };

  if (search) {
    query.search = search;
  } else {
    delete query.search;
  }

  if (sort) {
    query.sort = sort;
  } else {
    delete query.sort;
  }

  router.replace({ query });
};

const fetchCompanies = async (page, search, sort) => {
  const currentPage = page ?? pageNumber.value;
  const currentSearch = search ?? searchQuery.value;
  const currentSort = sort ?? sortParam.value;

  // Only update URL if arguments were explicitly provided
  if (page !== undefined || search !== undefined || sort !== undefined) {
    updateURLParams(currentPage, currentSearch, currentSort);
  }

  if (currentSearch) {
    await companiesStore.search({
      search: currentSearch,
      page: currentPage,
      sort: currentSort,
    });
  } else {
    await companiesStore.get({
      page: currentPage,
      sort: currentSort,
    });
  }
};

const clearSelection = () => {
  selectedCompanyIds.value = [];
};

const toggleSelectAll = shouldSelect => {
  const selectedIds = new Set(selectedCompanyIds.value);
  visibleCompanyIds.value.forEach(id =>
    shouldSelect ? selectedIds.add(id) : selectedIds.delete(id)
  );
  selectedCompanyIds.value = Array.from(selectedIds);
};

const toggleCompanySelection = ({ id, value }) => {
  const selectedIds = new Set(selectedCompanyIds.value);
  if (value) selectedIds.add(id);
  else selectedIds.delete(id);
  selectedCompanyIds.value = Array.from(selectedIds);
};

const openBulkDeleteDialog = () => bulkDeleteDialogRef.value?.open?.();

const deleteSelectedCompanies = async () => {
  if (!selectedCompanyIds.value.length) return;

  isBulkActionLoading.value = true;
  try {
    await Promise.all(
      selectedCompanyIds.value.map(companyId =>
        companiesStore.delete(companyId)
      )
    );
    useAlert(
      t('COMPANIES.BULK_ACTIONS.DELETE_SUCCESS', { count: selectedCount.value })
    );
    clearSelection();
    bulkDeleteDialogRef.value?.close?.();
    await fetchCompanies(pageNumber.value, searchValue.value, sortParam.value);
  } catch {
    useAlert(t('COMPANIES.BULK_ACTIONS.DELETE_ERROR'));
  } finally {
    isBulkActionLoading.value = false;
  }
};

const onSearch = debounce(query => {
  clearSelection();
  searchValue.value = query;
  fetchCompanies(1, query, sortParam.value);
}, DEBOUNCE_DELAY);

const onPageChange = page => {
  clearSelection();
  fetchCompanies(page, searchValue.value, sortParam.value);
};

const showCompany = companyId => {
  router.push({
    name: 'companies_dashboard_show',
    params: {
      accountId: route.params.accountId,
      companyId,
    },
  });
};

const openCreateCompanyDialog = () => {
  createCompanyDialogRef.value?.open();
};

const createCompany = async company => {
  try {
    const newCompany = await companiesStore.create(company);
    createCompanyDialogRef.value?.onSuccess();
    useAlert(t('COMPANIES.CREATE.MESSAGES.SUCCESS'));
    showCompany(newCompany.id);
  } catch {
    useAlert(t('COMPANIES.CREATE.MESSAGES.ERROR'));
  }
};

const handleSort = async ({ sort, order }) => {
  clearSelection();
  Object.assign(sortState, { activeSort: sort, activeOrdering: order });

  await updateUISettings({
    companies_sort_by: buildSortAttr(),
  });

  fetchCompanies(1, searchValue.value, buildSortAttr());
};

onMounted(() => {
  searchValue.value = searchQuery.value;

  if (!route.query.sort && sortParam.value !== DEFAULT_SORT_FIELD) {
    updateURLParams(pageNumber.value, searchQuery.value, sortParam.value);
  }

  fetchCompanies();
});
</script>

<template>
  <CompaniesListLayout
    :search-value="searchValue"
    :header-title="t('COMPANIES.HEADER')"
    :current-page="pageNumber"
    :total-items="Number(meta.totalCount || 0)"
    :active-sort="activeSort"
    :active-ordering="activeOrdering"
    :is-fetching-list="isFetchingList"
    :show-pagination-footer="!!companies.length"
    @update:current-page="onPageChange"
    @update:sort="handleSort"
    @search="onSearch"
    @create="openCreateCompanyDialog"
  >
    <div v-if="isFetchingList" class="flex items-center justify-center p-8">
      <span class="text-n-slate-11 text-base">{{
        t('COMPANIES.LOADING')
      }}</span>
    </div>
    <div
      v-else-if="companies.length === 0"
      class="flex items-center justify-center p-8"
    >
      <span class="text-n-slate-11 text-base">{{
        t('COMPANIES.EMPTY_STATE.TITLE')
      }}</span>
    </div>
    <div v-else class="flex flex-col gap-4">
      <div
        v-if="selectedCount"
        class="sticky top-0 z-10 flex items-center justify-between gap-3 rounded-lg border border-n-weak bg-n-surface-1 px-4 py-2 shadow-sm"
      >
        <span class="text-sm font-medium text-n-slate-12">
          {{ t('COMPANIES.BULK_ACTIONS.SELECTED', { count: selectedCount }) }}
        </span>
        <div class="flex items-center gap-2">
          <Button
            slate
            ghost
            sm
            :label="t('COMPANIES.BULK_ACTIONS.CLEAR')"
            @click="clearSelection"
          />
          <Button
            ruby
            ghost
            sm
            icon="i-lucide-trash"
            :label="t('COMPANIES.BULK_ACTIONS.DELETE')"
            :is-loading="isBulkActionLoading"
            @click="openBulkDeleteDialog"
          />
        </div>
      </div>
      <CompaniesTable
        :companies="companies"
        :selected-company-ids="selectedCompanyIds"
        :active-sort="activeSort"
        :active-ordering="activeOrdering"
        @show-company="showCompany"
        @toggle-company="toggleCompanySelection"
        @toggle-all="toggleSelectAll"
        @update:sort="handleSort"
      />
      <Dialog
        ref="bulkDeleteDialogRef"
        type="alert"
        :title="t('COMPANIES.BULK_ACTIONS.DIALOG_TITLE')"
        :description="
          t('COMPANIES.BULK_ACTIONS.DIALOG_DESCRIPTION', {
            count: selectedCount,
          })
        "
        :confirm-button-label="t('COMPANIES.BULK_ACTIONS.DELETE')"
        :is-loading="isBulkActionLoading"
        @confirm="deleteSelectedCompanies"
      />
    </div>
    <CompanyCreateDialog
      ref="createCompanyDialogRef"
      :is-loading="isCreatingCompany"
      @create="createCompany"
    />
  </CompaniesListLayout>
</template>
